import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Button, useTheme, Tabs, Tab, TextField, MenuItem, Table, TableHead, TableBody, TableRow, TableCell, Switch, Stack, Chip, TableContainer, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import CouponManagement from '../components/CouponManagement';
import AdminStatistics from '../components/AdminStatistics';
import ApiTester from '../components/ApiTester';
import { adminGetUsers, adminSetUserRole, adminGetTeams, adminSetTeamFrozen, adminListBugs, adminUpdateBug } from '../api';

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userRole, setUserRole] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [bugs, setBugs] = useState([]);
  const [bugsLoading, setBugsLoading] = useState(false);
  const [bugStatus, setBugStatus] = useState('open');
  const [bugQuery, setBugQuery] = useState('');
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Check for admin token/user
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    setLoading(false);
  }, [navigate]);


  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await adminGetUsers({ q: userQuery || undefined, role: userRole || undefined });
      setUsers(data);
    } catch (e) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadTeams = async () => {
    setTeamsLoading(true);
    try {
      const data = await adminGetTeams();
      setTeams(data);
    } catch (e) {
      setTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  const loadBugs = async () => {
    setBugsLoading(true);
    try {
      const data = await adminListBugs({ status: bugStatus || undefined, q: bugQuery || undefined });
      setBugs(data);
    } catch (e) {
      setBugs([]);
    } finally {
      setBugsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) return <Box sx={{ mt: 8, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: { xs: 2, sm: 6 }, px: { xs: 2, sm: 0 }, background: theme.palette.background.default, minHeight: '100vh', color: theme.palette.text.primary }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 4, background: theme.palette.background.paper, color: theme.palette.text.primary, border: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>Admin Dashboard</Typography>
        
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="Statistics" />
          <Tab label="Users" />
          <Tab label="Teams" />
          <Tab label="Bug Reports" />
          <Tab label="Coupon Management" />
          <Tab label="API Tester" />
        </Tabs>

        {activeTab === 0 && <AdminStatistics onLogout={handleLogout} />}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Users</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField label="Search" value={userQuery} onChange={e => setUserQuery(e.target.value)} size="small" />
              <TextField select label="Role" value={userRole} onChange={e => setUserRole(e.target.value)} size="small" sx={{ minWidth: 160 }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="athlete">Athlete</MenuItem>
                <MenuItem value="coach">Coach</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
              <Button variant="contained" onClick={loadUsers}>Search</Button>
            </Stack>
            {usersLoading ? <CircularProgress /> : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: isMobile ? 120 : 'auto' }}>Name</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 180 : 'auto' }}>Email</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 'auto' }}>Role</TableCell>
                      <TableCell align={isMobile ? 'left' : 'right'} sx={{ minWidth: isMobile ? 150 : 'auto' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id}>
                        <TableCell>{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.role}</TableCell>
                        <TableCell>
                          <TextField select size="small" value={u.role} onChange={async (e) => {
                            const newRole = e.target.value;
                            await adminSetUserRole(u.id, newRole);
                            loadUsers();
                          }} sx={{ minWidth: 140, width: '100%' }}>
                            <MenuItem value="athlete">Athlete</MenuItem>
                            <MenuItem value="coach">Coach</MenuItem>
                            <MenuItem value="both">Both</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                          </TextField>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Teams</Typography>
            <Button variant="outlined" onClick={loadTeams} sx={{ mb: 2 }}>Refresh</Button>
            {teamsLoading ? <CircularProgress /> : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: isMobile ? 120 : 'auto' }}>Name</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 'auto' }}>Plan</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 100 : 'auto' }}>Renewal</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 'auto' }}>Coaches</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 'auto' }}>Athletes</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 'auto' }}>Frozen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teams.map(t => (
                      <TableRow key={t.id}>
                        <TableCell>{t.name}</TableCell>
                        <TableCell>{t.plan_name || '-'}</TableCell>
                        <TableCell>{t.renewal_date ? new Date(t.renewal_date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>{t.usage.coaches.current}/{t.usage.coaches.max ?? '-'}</TableCell>
                        <TableCell>{t.usage.athletes.current}/{t.usage.athletes.max ?? '-'}</TableCell>
                        <TableCell>
                          <Switch onChange={async (e) => {
                            await adminSetTeamFrozen(t.id, e.target.checked);
                            loadTeams();
                          }} checked={!!t.frozen} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {activeTab === 3 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Bug Reports</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField label="Search" value={bugQuery} onChange={e => setBugQuery(e.target.value)} size="small" />
              <TextField select label="Status" value={bugStatus} onChange={e => setBugStatus(e.target.value)} size="small" sx={{ minWidth: 160 }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </TextField>
              <Button variant="contained" onClick={loadBugs}>Search</Button>
            </Stack>
            {bugsLoading ? <CircularProgress /> : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: isMobile ? 200 : 'auto' }}>Title</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 'auto' }}>Severity</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 100 : 'auto' }}>Page</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 150 : 'auto' }}>User</TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 80 : 'auto' }}>Status</TableCell>
                      <TableCell align={isMobile ? 'left' : 'right'} sx={{ minWidth: isMobile ? 150 : 'auto' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bugs.map(b => (
                      <TableRow key={b.id}>
                        <TableCell sx={{ maxWidth: { xs: 200, sm: 300 }, overflow: 'hidden' }}>
                          <Typography fontWeight={600} sx={{ fontSize: isMobile ? '0.875rem' : '1rem', mb: 0.5 }}>{b.title}</Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word'
                            }}
                            title={b.description}
                          >
                            {b.description}
                          </Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={b.severity} color={b.severity === 'critical' ? 'error' : b.severity === 'high' ? 'warning' : 'default'} /></TableCell>
                        <TableCell>{b.page || '-'}</TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>{b.user_name ? `${b.user_name} (${b.user_email})` : 'Anonymous'}</TableCell>
                        <TableCell>{b.status}</TableCell>
                        <TableCell>
                          <TextField select size="small" value={b.status} onChange={async (e) => {
                            await adminUpdateBug(b.id, { status: e.target.value });
                            loadBugs();
                          }} sx={{ minWidth: { xs: '100%', sm: 160 }, width: { xs: '100%', sm: 'auto' } }}>
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="resolved">Resolved</MenuItem>
                            <MenuItem value="closed">Closed</MenuItem>
                          </TextField>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {activeTab === 4 && (
          <CouponManagement />
        )}

        {activeTab === 5 && (
          <ApiTester />
        )}
      </Paper>
    </Box>
  );
}
